import React, { useEffect, useRef } from 'react';
import { DatePicker, Form, Input, Select, Switch, Row, Col } from 'antd';
import { FileOutlined, LinkOutlined, PaperClipOutlined, SendOutlined } from '@ant-design/icons';
import AppModal from '@/pages/inside/tenant/AppModal';
import { Button } from '@/components/ui/Button';
import RichTextEditorWithAttachments from '@/components/ui/RichTextEditorWithAttachments';
import { Container } from '@/components/ui/Container';
import StatusBadge from '@/components/ui/StatusBadge';
import { formatDate, getAttachmentFileExtension } from '@hooks/projects/projectHelpers';
import { MILESTONE_STATUS_API_VALUES, MILESTONE_STATUS_RULE_MESSAGE } from '@hooks/projects/useProjectMilestones';
import TaskAttachmentTile from '@/components/module/projects/task/TaskAttachmentTile';
import styled from 'styled-components';
import {
    AttachmentRemoveBtn,
    AttachmentThumbnailBox,
    AttachmentThumbnailFileExt,
    AttachmentThumbnailFileWrap,
    AttachmentThumbnailImg,
    CreateRoleModalBody,
    CreateRoleModalFooter,
    DetailsAttachmentGridScroll,
    ModalChromeHeading,
    MutedText,
    PanelSectionLabel,
} from '@/pages/inside/tenant/style';
import {
    projectSprintTaskModalFormStyles,
    submissionRichDescriptionFormItemStyle,
    submissionRichDescriptionLabelStyle,
} from '@/pages/inside/tenant/project/projectTabStyles';

const MILESTONE_DESCRIPTION_PLACEHOLDER =
    'Add description — bold, lists, @mentions. Attach files or links.';

function MilestoneAttachmentsSection({ sections, setPreviewImageUrl, openAttachment, marginTop = '20px' }) {
    if (!sections) return null;
    return (
        <>
            {sections.files && sections.files.length > 0 && (
                <Container $marginTop={marginTop}>
                    <PanelSectionLabel>Attachments ({sections.files.length})</PanelSectionLabel>
                    <DetailsAttachmentGridScroll style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                        {sections.files.map((file) => (
                            <TaskAttachmentTile
                                key={file.id}
                                href={file.url}
                                title={file.title || 'File'}
                                isImage={file.isImage}
                                ext={file.ext}
                                onPreviewImage={file.isImage && setPreviewImageUrl ? (url) => setPreviewImageUrl(url) : undefined}
                                onOpenFile={openAttachment}
                                style={{ width: '100%', height: '110px' }}
                            />
                        ))}
                    </DetailsAttachmentGridScroll>
                </Container>
            )}
            {sections.links && sections.links.length > 0 && (
                <div style={{ marginTop }}>
                    <PanelSectionLabel
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            marginBottom: 8,
                            fontSize: 11,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            opacity: 0.75,
                        }}
                    >
                        <LinkOutlined />
                        Links ({sections.links.length})
                    </PanelSectionLabel>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {sections.links.map((link) => (
                            <a
                                key={link.id}
                                href={link.href || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '4px 10px',
                                    borderRadius: 999,
                                    border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                    background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                                    fontSize: 12,
                                    color: '#dc0d2e',
                                    textDecoration: 'none',
                                }}
                            >
                                {link.title || link.href}
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
}

export default function MilestoneModal(props) {
    const { mode } = props;

    const isEdit = mode === 'edit';

    if (mode === 'view') {
        const {
            selectedMilestone,
            selectedMilestoneDescription,
            selectedMilestoneAttachmentSections,
            setPreviewImageUrl,
            openAttachment,
            open,
            onCancel,
        } = props;

        return (
            <AppModal
                size="md"
                title={selectedMilestone && (
                    <Container $display="flex" $justifyContent="space-between" $alignItems="flex-start" $width="95%">
                        <div>
                            <Container $marginBottom="4px">{selectedMilestone.name || 'Milestone Detail'}</Container>
                            <MutedText>Due {formatDate(selectedMilestone.dueDate) || '-'}</MutedText>
                        </div>
                        <StatusBadge status={selectedMilestone.status} />
                    </Container>
                )}
                open={open}
                onCancel={onCancel}
                footer={null}
                styles={{ mask: { backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', background: 'rgba(0, 0, 0, 0.45)' } }}
            >
                {selectedMilestone && (
                    <>
                        <CreateRoleModalBody>
                            {!!selectedMilestoneDescription && (
                                <Container $marginBottom="24px">
                                    <PanelSectionLabel>Description</PanelSectionLabel>
                                    <Container
                                        className="markdown-preview"
                                        onClick={(e) => {
                                            const target = e.target.closest('a');
                                            if (!target) return;
                                            const href = target.getAttribute('href');
                                            if (!href) return;
                                            
                                            e.preventDefault();
                                            e.stopPropagation();
                                            
                                            const fullname = target.getAttribute('data-attachment-fullname') || '';
                                            const lowerHref = href.toLowerCase();
                                            const isImageByExt = fullname.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) || lowerHref.match(/\.(jpg|jpeg|png|gif|webp|svg)(?:[?#].*)?$/i);
                                            if (isImageByExt || lowerHref.startsWith('data:image/')) {
                                                setPreviewImageUrl(href);
                                            } else {
                                                if (openAttachment) openAttachment(href);
                                            }
                                        }}
                                    >
                                        <style>{`
                                            .markdown-preview a {
                                                color: #dc0d2e;
                                                text-decoration: none;
                                            }
                                            .markdown-preview a:hover {
                                                text-decoration: underline;
                                            }
                                        `}</style>
                                        <div dangerouslySetInnerHTML={{ __html: selectedMilestoneDescription }} />
                                    </Container>
                                </Container>
                            )}
                            <MilestoneAttachmentsSection
                                sections={selectedMilestoneAttachmentSections}
                                setPreviewImageUrl={setPreviewImageUrl}
                                openAttachment={openAttachment}
                                marginTop="0"
                            />
                        </CreateRoleModalBody>
                        <CreateRoleModalFooter>
                            <Button $primary $blackOnLight $size="14px" onClick={onCancel}>Close</Button>
                        </CreateRoleModalFooter>
                    </>
                )}
            </AppModal>
        );
    }

    const {
        open,
        onCancel,
        milestoneForm,
        dueDateRules,
        editMilestoneForm,
        editDueDateRules = [],
        disabledDateForPicker,
        phaseOptions = [],
        statusOptions = [],
        mentionUsers,
        mentionMilestones,
        mentionSprints,
        selectedMilestoneAttachmentSections = { files: [], links: [] },
        onRemoveExistingAttachment,
        setPreviewImageUrl,
        openAttachment,
        createPending,
        handleCreateMilestoneAction,
        updateMilestonePending,
        handleEditMilestoneSubmit,
        handlePostFilesSelected,
        handleLinkRequest,
        pendingPostAttachments = [],
        removePendingPostAttachment,
        pendingLinkAttachments = [],
        removePendingLinkAttachment,
        setLiveEditDescription,
    } = props;

    const form = isEdit ? editMilestoneForm : milestoneForm;
    const nameValue = Form.useWatch('name', form) || '';
    const rulesForDueDate = isEdit ? editDueDateRules : dueDateRules;
    const title = isEdit ? 'Edit Milestone' : 'Create Milestone';
    const primaryCtaLabel = isEdit ? 'Save changes' : 'Create';
    const primaryCtaPending = isEdit ? updateMilestonePending : createPending;
    const onSubmit = isEdit ? handleEditMilestoneSubmit : handleCreateMilestoneAction;

    useEffect(() => {
        if (!open) return;
        const id = requestAnimationFrame(() => {
            document
                .querySelectorAll('.ant-modal-wrap.modal-size-md.ant-modal-wrap-open .ant-modal-body')
                .forEach((el) => {
                    el.scrollTop = 0;
                });
        });
        return () => cancelAnimationFrame(id);
    }, [open]);

    const modalTitle = <ModalChromeHeading>{title}</ModalChromeHeading>;

    return (
        <AppModal
            size="md"
            title={modalTitle}
            open={open}
            onCancel={onCancel}
            footer={
                <CreateRoleModalFooter $inFooterSlot>
                    <Button $primary={false} $size="14px" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button
                        $primary
                        $blackOnLight
                        $size="14px"
                        loading={primaryCtaPending}
                        icon={<SendOutlined />}
                        onClick={onSubmit}
                    >
                        {primaryCtaLabel}
                    </Button>
                </CreateRoleModalFooter>
            }
        >
            <CreateRoleModalBody $flush $noMaxHeight style={{ paddingBottom: 28 }}>
                <Form
                    form={form}
                    layout="vertical"
                    requiredMark={false}
                    styles={projectSprintTaskModalFormStyles}
                    onValuesChange={
                        isEdit
                            ? (changedValues, allValues) => {
                                  if (
                                      'description' in changedValues &&
                                      typeof setLiveEditDescription === 'function'
                                  ) {
                                      setLiveEditDescription(allValues.description || '');
                                  }
                              }
                            : undefined
                    }
                >
                    <Row gutter={[16, 8]}>
                        <Col xs={24} md={14}>
                            <Form.Item
                                name="name"
                                label={<span style={submissionRichDescriptionLabelStyle}>Name</span>}
                                rules={[{ required: true, message: 'Enter milestone name' }]}
                                style={{ marginBottom: 4 }}
                            >
                                <Input placeholder="e.g. Milestone 1" size="large" maxLength={45} />
                            </Form.Item>
                            <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--ant-color-text-description, #8c8c8c)', marginBottom: 12 }}>
                                {nameValue.length}/45
                            </div>
                        </Col>
                        <Col xs={24} md={10}>
                            <Form.Item
                                name="dueDate"
                                label={<span style={submissionRichDescriptionLabelStyle}>Due date</span>}
                                rules={rulesForDueDate}
                            >
                                <DatePicker
                                    size="large"
                                    format="YYYY-MM-DD"
                                    style={{ width: '100%' }}
                                    allowClear
                                    disabledDate={disabledDateForPicker}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    {isEdit && (
                        <Container
                            $display="flex"
                            $flexDirection="column"
                            style={{ gap: 16, marginTop: 8 }}
                        >
                            <Row gutter={[16, 8]} style={{ marginBottom: 0 }}>
                                <Col xs={24} md={14}>
                                    <Form.Item
                                        name="phaseId"
                                        label={<span style={submissionRichDescriptionLabelStyle}>Phase</span>}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Select
                                            allowClear
                                            placeholder="Unassigned"
                                            options={phaseOptions}
                                            size="large"
                                            showSearch
                                            optionFilterProp="label"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={10}>
                                    <Form.Item
                                        name="status"
                                        label={<span style={submissionRichDescriptionLabelStyle}>Status</span>}
                                        style={{ marginBottom: 0 }}
                                        rules={[
                                            { required: true, message: 'Select status' },
                                            {
                                                type: 'enum',
                                                enum: MILESTONE_STATUS_API_VALUES,
                                                message: MILESTONE_STATUS_RULE_MESSAGE,
                                            },
                                        ]}
                                    >
                                        <Select options={statusOptions} size="large" placeholder="Status" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item
                                name="isActive"
                                label={<span style={submissionRichDescriptionLabelStyle}>Active</span>}
                                valuePropName="checked"
                                style={{ marginBottom: 0 }}
                            >
                                <Switch />
                            </Form.Item>
                        </Container>
                    )}
                    <Form.Item
                        label={<span style={submissionRichDescriptionLabelStyle}>Description</span>}
                        style={submissionRichDescriptionFormItemStyle}
                    >
                        <RichTextEditorWithAttachments
                            form={form}
                            fieldName="description"
                            isEdit={isEdit}
                            open={open}
                            mentionUsers={mentionUsers}
                            mentionMilestones={mentionMilestones}
                            mentionSprints={mentionSprints}
                            placeholder={MILESTONE_DESCRIPTION_PLACEHOLDER}
                            setLiveEditDescription={setLiveEditDescription}
                            
                            pendingFiles={pendingPostAttachments}
                            onFilesSelected={handlePostFilesSelected}
                            onRemovePendingFile={(id) => removePendingPostAttachment(id, isEdit)}
                            
                            pendingLinks={pendingLinkAttachments}
                            onRemovePendingLink={removePendingLinkAttachment}
                            onLinkRequest={handleLinkRequest}
                            
                            existingFiles={selectedMilestoneAttachmentSections?.files || []}
                            existingLinks={selectedMilestoneAttachmentSections?.links || []}
                            onRemoveExistingAttachment={onRemoveExistingAttachment}
                        />
                    </Form.Item>
                </Form>
            </CreateRoleModalBody>
        </AppModal>
    );
}
